/**
 * Measuring a stroke, once.
 *
 * Bounds and the point list are needed on every frame a stroke is on screen, and both
 * are the same for the life of the stroke — a stroke is never edited after it is
 * drawn. Working them out again each frame is exactly the kind of cost that a
 * three-hour lesson multiplies into a stutter, so they are remembered instead.
 */

import type { Rect, Stroke } from '@opal/shared';

import { strokePoints, type InkPoint } from './ink.js';
import { simplifyPoints } from './simplify.js';

const boundsCache = new WeakMap<Stroke, Rect>();
const pointsCache = new WeakMap<Stroke, InkPoint[]>();
const simplifiedCache = new WeakMap<Stroke, Map<number, InkPoint[]>>();

/** Tolerances are bucketed so that a small zoom change reuses the same simplification. */
function bucket(tolerance: number): number {
  return Math.round(tolerance * 100) / 100;
}

/**
 * The stroke as it should be drawn at this zoom, worked out once per zoom level.
 *
 * Simplifying is cheap for one stroke and ruinous for a board full of them on every
 * frame, so each answer is kept for as long as the stroke lives.
 */
export function simplifiedPointsOf(stroke: Stroke, tolerance: number): InkPoint[] {
  const key = bucket(tolerance);
  const forStroke = simplifiedCache.get(stroke) ?? new Map<number, InkPoint[]>();
  const cached = forStroke.get(key);
  if (cached) return cached;

  const simplified = simplifyPoints(strokePointsOf(stroke), key);
  forStroke.set(key, simplified);
  simplifiedCache.set(stroke, forStroke);
  return simplified;
}

/** The stroke's points, read from its flat triplets the first time and kept. */
export function strokePointsOf(stroke: Stroke): InkPoint[] {
  const cached = pointsCache.get(stroke);
  if (cached) return cached;
  const points = strokePoints(stroke);
  pointsCache.set(stroke, points);
  return points;
}

/** The canvas-space box the stroke covers, ink width included. */
export function strokeBounds(stroke: Stroke): Rect {
  const cached = boundsCache.get(stroke);
  if (cached) return cached;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const point of strokePointsOf(stroke)) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }
  if (!Number.isFinite(minX)) {
    const empty: Rect = { x: 0, y: 0, width: 0, height: 0 };
    boundsCache.set(stroke, empty);
    return empty;
  }

  // Ink is drawn either side of the line, so the box grows by half its width.
  const pad = stroke.canvasWidth;
  const bounds: Rect = {
    x: minX - pad,
    y: minY - pad,
    width: maxX - minX + pad * 2,
    height: maxY - minY + pad * 2,
  };
  boundsCache.set(stroke, bounds);
  return bounds;
}
